import { motion } from 'framer-motion';
import { Cat, Sparkles, Wallet, Star, Clock, CreditCard, HashIcon, MessageSquare } from 'lucide-react';
import { formatEther } from 'viem';
import { shortenAddress } from '../utils';
import { MintPhaseIndicator, MintPhase } from './MintPhaseIndicator';
import {  ReactNode, useState, useEffect } from 'react';
import React from 'react';
import type { LucideIcon } from 'lucide-react';

// Define card animation variants without the jittering effect
const cardVariants = {
  initial: { scale: 0.95, opacity: 0.8 },
  animate: { 
    scale: 1, 
    opacity: 1,
    transition: { 
      duration: 0.5,
      ease: "easeOut"
    }
  },
  hover: { 
    scale: 1.02,
    transition: { 
      duration: 0.3,
    }
  },
  tap: {
    scale: 0.98,
  }
};

// Define phase information
type PhaseInfoType = Record<number, {
  name: string;
  color: string;
  glowColor: string;
  gradient: string;
  icon: LucideIcon;
  status?: string;
}>;

const phaseInfo: PhaseInfoType = {
  [MintPhase.NONE]: {
    name: "Pre-Mint Phase",
    color: "#777777",
    glowColor: "#555555",
    gradient: "linear-gradient(to right, #333333, #555555)",
    icon: Clock,
    status: "Not Started"
  },
  [MintPhase.Whitelist]: {
    name: "FCFS WL Phase",
    color: "#e879f9",
    glowColor: "var(--neon-purple)",
    gradient: "linear-gradient(to right, #e879f9, #c026d3)",
    icon: Star,
    status: "SOLD OUT!"
  },
  [MintPhase.GTD]: {
    name: "GTD Phase",
    color: "#facc15",
    glowColor: "var(--neon-yellow)",
    gradient: "linear-gradient(to right, #facc15, #eab308)",
    icon: Sparkles,
    status: "Skipped - Sold Out in WL"
  },
  [MintPhase.Public]: {
    name: "Public Phase",
    color: "#22c55e",
    glowColor: "var(--neon-green)",
    gradient: "linear-gradient(to right, #22c55e, #10b981)",
    icon: Cat,
    status: "Skipped - Sold Out in WL"
  }
};

// Cat ear animation - still keeping this for visual interest
const catEarVariants = {
  initial: { rotate: 0 },
  wiggle: {
    rotate: [0, 5, 0, -5, 0],
    transition: {
      repeat: Infinity,
      repeatType: "mirror" as const,
      duration: 1.5,
      ease: "easeInOut",
      repeatDelay: 3,
    }
  }
};

interface HeaderProps {
  mintPrice: bigint | undefined;
  totalSupply: bigint | string | undefined;
  maxSupply: bigint | string | undefined;
  isConnected: boolean;
  isConnecting: boolean;
  address: string | undefined;
  isLoading: boolean;
  isConfirming: boolean;
  isPending: boolean;
  onConnect: () => void;
  onMint: () => void;
  hasNFTs?: boolean;  
  isWhitelisted?: boolean | null;
  currentPhase: number;
  isMintingEnabled?: boolean;
  nextPhaseToggleTimestamp?: bigint;
  revealTimestamp?: { revealed: boolean; timestamp: bigint };
  charactersEnabled?: boolean;
  onToggleCharacters?: () => void;
  gtdEligibleCount?: number;
}

// Type for countdown time
interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export function Header({
  mintPrice,
  totalSupply,
  maxSupply,
  isConnected,
  isConnecting,
  address,
  isLoading,
  isConfirming,
  isPending,
  onConnect,
  onMint,
  hasNFTs = false,  
  isWhitelisted = null,
  currentPhase = MintPhase.NONE,
  isMintingEnabled = true,
  nextPhaseToggleTimestamp,
  revealTimestamp,
  charactersEnabled = false,
  onToggleCharacters = () => {},
  gtdEligibleCount = 0,
}: HeaderProps) {
  // State for timers that update every second
  const [phaseTimeLeft, setPhaseTimeLeft] = useState<TimeLeft | null>(null);
  const [revealTimeLeft, setRevealTimeLeft] = useState<TimeLeft | null>(null);
  const [mintStartTimeLeft, setMintStartTimeLeft] = useState<TimeLeft | null>(null);
  const [currentTime, setCurrentTime] = useState<number>(Math.floor(Date.now() / 1000));
  const [rainbowHue, setRainbowHue] = useState(0);
  const [progressBarHue, setProgressBarHue] = useState(120); // Start with green (120 degrees in HSL)

  // Format mint price as a string to avoid bigint JSX issues
  const formattedMintPrice = mintPrice ? formatEther(mintPrice) : '0';

  // Function to safely convert bigint to string to avoid JSX rendering issues
  const safelyRenderBigInt = (value: bigint | undefined): string => {
    return value !== undefined ? value.toString() : '0';
  };

  // Additional function that safely handles any value that might be a bigint
  const renderSafeValue = (value: any): React.ReactNode => {
    if (value === undefined || value === null) {
      return "???";
    }
    if (typeof value === 'bigint') {
      return value.toString();
    }
    return String(value);
  };

  const handleSpecialAccess = () => {
    window.open('https://kingdom.purrtardio.com', '_blank');
  };

  // Function to format the phase name properly
  const getPhaseName = () => {
    switch(currentPhase) {
      case MintPhase.NONE:
        return "No Active Phase";
      case MintPhase.Whitelist:
        return "Whitelist Phase";
      case MintPhase.GTD:
        return "GTD Phase";
      case MintPhase.Public:
        return "Public Phase";
      default:
        return "Unknown Phase";
    }
  };

  // Function to get current phase number and total
  const getPhaseProgress = () => {
    if (currentPhase === MintPhase.NONE) return "";
    
    const currentPhaseNumber = 
      currentPhase === MintPhase.GTD ? 1 : 
      currentPhase === MintPhase.Whitelist ? 2 : 
      currentPhase === MintPhase.Public ? 3 : 0;
    
    return `${currentPhaseNumber}/3`;
  };

  // Function to get phase progress percentage
  const getPhaseProgressPercentage = () => {
    if (currentPhase === MintPhase.NONE) return 0;
    
    return currentPhase === MintPhase.GTD ? 33.33 : 
           currentPhase === MintPhase.Whitelist ? 66.67 : 
           currentPhase === MintPhase.Public ? 100 : 0;
  };

  // Set up intervals to update the countdown timers every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Math.floor(Date.now() / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Rainbow effect for Public phase
  useEffect(() => {
    if (currentPhase !== MintPhase.Public) return;
    
    const rainbowInterval = setInterval(() => {
      setRainbowHue(prev => (prev + 1) % 360);
    }, 50);
    
    return () => clearInterval(rainbowInterval);
  }, [currentPhase]);

  // Update color shift for progress bar
  useEffect(() => {
    const colorInterval = setInterval(() => {
      setProgressBarHue(prev => (prev + 1) % 360);
    }, 50);
    
    return () => clearInterval(colorInterval);
  }, []);

  // Update phase countdown when current phase timestamp changes
  useEffect(() => {
    if (!nextPhaseToggleTimestamp) {
      
      setPhaseTimeLeft(null);
      return;
    }
    
    // Convert bigint to number safely - use a try/catch to be extra safe
    let targetTime;
    try {
      targetTime = Number(nextPhaseToggleTimestamp.toString());
    } catch (error) {
      console.error('[DEBUG Header] Error converting nextPhaseToggleTimestamp:', error);
      setPhaseTimeLeft(null);
      return;
    }
    
    if (targetTime <= currentTime) {
      setPhaseTimeLeft(null);
      return;
    }
    
    const difference = targetTime - currentTime;
    const days = Math.floor(difference / 86400);
    const hours = Math.floor((difference % 86400) / 3600);
    const minutes = Math.floor((difference % 3600) / 60);
    const seconds = Math.floor(difference % 60);
    
    setPhaseTimeLeft({ days, hours, minutes, seconds });
  
  }, [currentTime, nextPhaseToggleTimestamp, currentPhase, getPhaseName]);

  // Update mint start countdown when in NONE phase
  useEffect(() => {
    if (currentPhase !== MintPhase.NONE || !import.meta.env.VITE_MINT_START) {
      setMintStartTimeLeft(null);
      return;
    }
    
    // Parse ISO date string into timestamp (seconds)
    const targetTime = Math.floor(new Date(import.meta.env.VITE_MINT_START).getTime() / 1000);
    if (targetTime <= currentTime) {
      setMintStartTimeLeft(null);
      return;
    }
    
    const difference = targetTime - currentTime;
    const days = Math.floor(difference / 86400);
    const hours = Math.floor((difference % 86400) / 3600);
    const minutes = Math.floor((difference % 3600) / 60);
    const seconds = Math.floor(difference % 60);
    
    setMintStartTimeLeft({ days, hours, minutes, seconds });
  }, [currentTime, currentPhase]);

  // Update reveal countdown whenever currentTime changes
  useEffect(() => {
    if (!revealTimestamp || revealTimestamp.revealed) {
      setRevealTimeLeft(null);
      return;
    }
    
    const targetTime = Number(revealTimestamp.timestamp.toString());
    if (targetTime <= currentTime) {
      setRevealTimeLeft(null);
      return;
    }
    
    const difference = targetTime - currentTime;
    const days = Math.floor(difference / 86400);
    const hours = Math.floor((difference % 86400) / 3600);
    const minutes = Math.floor((difference % 3600) / 60);
    const seconds = Math.floor(difference % 60);
    
    setRevealTimeLeft({ days, hours, minutes, seconds });
  }, [currentTime, revealTimestamp]);

  // Format the countdown display for the UI
  const formatCountdown = (timeLeft: TimeLeft | null): string => {
    if (!timeLeft) return "...";
    
    try {
      const { days, hours, minutes, seconds } = timeLeft;
      const dayDisplay = days > 0 ? `${days}d ` : '';
      const formatted = `${dayDisplay}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      return formatted;
    } catch (error) {
      console.error('[DEBUG] Error formatting countdown:', error);
      return "...";
    }
  };

  // Calculate progress percentage safely
  const calculateProgressPercentage = (): number => {
    if (!totalSupply || !maxSupply) return 0;
    
    const total = Number(String(totalSupply));
    const max = Number(String(maxSupply));
    
    return Math.min(100, (total * 100) / max);
  };


  return (
    <header className="text-center mb-16">
      <motion.h1 
        className="text-6xl font-bold mb-4 glitch-text"
        initial={{ scale: 0.5 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
      >
        <span className="text-primary">Purrtardio</span> NFT Collection
      </motion.h1>
      <motion.p 
        className="text-3xl mb-4 text-neon-purple font-bold glow-text"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        SOLD OUT!
      </motion.p>      
      
      <MintPhaseIndicator currentPhase={currentPhase as MintPhase} gtdEligibleCount={gtdEligibleCount} />
      
      {/* Collection Info Cards - Redesigned */}
      <div className="flex justify-center gap-6 flex-wrap mb-10">
        {/* Card 1: Collection Status */}
        <motion.div 
          className="info-card relative min-w-[200px] h-40 p-5 rounded-lg border-0 flex flex-col justify-center items-center text-lg bg-gradient-to-br from-primary/20 to-neon-purple/20 overflow-hidden shadow-lg"
          initial="initial"
          animate="animate"
          whileHover="hover"
          whileTap="tap"
          variants={cardVariants}
        >
          {/* LED pulse border effect */}
          <div className="absolute inset-0 pointer-events-none">
            <motion.div 
              className="absolute inset-0 rounded-lg border-2 border-primary/70"
              animate={{
                boxShadow: [
                  "0 0 2px var(--primary), 0 0 4px var(--primary)",
                  "0 0 6px var(--primary), 0 0 12px var(--neon-purple)",
                  "0 0 2px var(--primary), 0 0 4px var(--primary)"
                ]
              }}
              transition={{ 
                duration: 3, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
            />
          </div>
          
          <motion.div 
            className="cat-ears mb-2"
            variants={catEarVariants}
            initial="initial"
            animate="wiggle"
          >
            <Cat className="h-8 w-8 text-primary" />
          </motion.div>
          <h3 className="font-bold mb-2">Collection Status</h3>
          <p className="text-2xl font-bold text-neon-purple glow-text">MINTED OUT!</p>
          <p className="text-sm mt-2">Sold out in FCFS WL Phase</p>
        </motion.div>
        
        {/* Card 2: Final Price Info */}
        <motion.div 
          className="info-card relative min-w-[200px] h-40 p-5 rounded-lg border-0 flex flex-col justify-center items-center text-lg bg-gradient-to-br from-secondary/20 to-neon-blue/20 overflow-hidden shadow-lg"
          initial="initial"
          animate="animate"
          whileHover="hover"
          whileTap="tap"
          variants={cardVariants}
        >
          {/* LED pulse border effect */}
          <div className="absolute inset-0 pointer-events-none">
            <motion.div 
              className="absolute inset-0 rounded-lg border-2 border-secondary/70"
              animate={{
                boxShadow: [
                  "0 0 2px var(--secondary), 0 0 4px var(--secondary)",
                  "0 0 6px var(--secondary), 0 0 12px var(--neon-blue)",
                  "0 0 2px var(--secondary), 0 0 4px var(--secondary)"
                ]
              }}
              transition={{ 
                duration: 3, 
                repeat: Infinity, 
                ease: "easeInOut",
                delay: 0.5
              }}
            />
          </div>
          
          <motion.div 
            className="cat-ears mb-2"
            variants={catEarVariants}
            initial="initial"
            animate="wiggle"
          >
            <CreditCard className="h-8 w-8 text-secondary" />
          </motion.div>
          <h3 className="font-bold mb-2">Final Mint Price</h3>
          <p className="text-2xl font-bold glow-text">
            {formattedMintPrice} HYPE
            <span className="text-xs text-neon-pink ml-1 block">(PURRDOM-LIST)</span>
          </p>
        </motion.div>

        {/* Card 3: Final Supply */}
        <motion.div 
          className="info-card relative min-w-[200px] h-40 p-5 rounded-lg border-0 flex flex-col justify-center items-center text-lg bg-gradient-to-br from-neon-green/20 to-neon-blue/20 overflow-hidden shadow-lg"
          initial="initial"
          animate="animate"
          whileHover="hover"
          whileTap="tap"
          variants={cardVariants}
        >
          {/* LED pulse border effect */}
          <div className="absolute inset-0 pointer-events-none">
            <motion.div 
              className="absolute inset-0 rounded-lg border-2 border-neon-green/70"
              animate={{
                boxShadow: [
                  "0 0 2px var(--neon-green), 0 0 4px var(--neon-green)",
                  "0 0 6px var(--neon-green), 0 0 12px var(--neon-blue)",
                  "0 0 2px var(--neon-green), 0 0 4px var(--neon-green)"
                ]
              }}
              transition={{ 
                duration: 3, 
                repeat: Infinity, 
                ease: "easeInOut",
                delay: 1
              }}
            />
          </div>
          
          <motion.div 
            className="cat-ears mb-2"
            variants={catEarVariants}
            initial="initial"
            animate="wiggle"
          >
            <HashIcon className="h-8 w-8 text-neon-green" />
          </motion.div>
          <h3 className="font-bold mb-2">Total Supply</h3>
          <p className="text-2xl font-bold glow-text">
            {maxSupply !== undefined ? (typeof maxSupply === 'bigint' ? maxSupply.toString() : String(maxSupply)) : "???"} / {maxSupply !== undefined ? (typeof maxSupply === 'bigint' ? maxSupply.toString() : String(maxSupply)) : "???"}
          </p>
          <p className="text-sm mt-2 text-neon-green">100% Minted</p>
        </motion.div>

        {/* Card 4: Phase Countdown */}
        {currentPhase !== MintPhase.Public && (
          <motion.div 
            className="info-card relative min-w-[200px] h-40 p-5 rounded-lg border-0 flex flex-col justify-center items-center text-lg bg-gradient-to-br from-neon-purple/20 to-neon-blue/20 overflow-hidden shadow-lg"
            initial="initial"
            animate="animate"
            whileHover="hover"
            whileTap="tap"
            variants={cardVariants}
          >
            {/* LED pulse border effect */}
            <div className="absolute inset-0 pointer-events-none">
              <motion.div 
                className="absolute inset-0 rounded-lg border-2 border-neon-purple/70"
                animate={{
                  boxShadow: [
                    "0 0 2px var(--neon-purple), 0 0 4px var(--neon-purple)",
                    "0 0 6px var(--neon-purple), 0 0 12px var(--neon-blue)",
                    "0 0 2px var(--neon-purple), 0 0 4px var(--neon-purple)"
                  ]
                }}
                transition={{ 
                  duration: 3, 
                  repeat: Infinity, 
                  ease: "easeInOut",
                  delay: 1.5
                }}
              />
            </div>
            
            <motion.div 
              className="cat-ears mb-2"
              variants={catEarVariants}
              initial="initial"
              animate="wiggle"
            >
              <Clock className="h-8 w-8 text-neon-purple" />
            </motion.div>
            <h3 className="font-bold mb-2">
              {currentPhase === MintPhase.NONE 
                ? "Mint Starts In" 
                : currentPhase === MintPhase.GTD
                  ? "GTD Phase Ends" 
                  : currentPhase === MintPhase.Whitelist 
                    ? "WL Phase Ends" 
                    : "Next Phase"}
            </h3>
            
            {currentPhase === MintPhase.NONE ? (
              <p className="text-2xl font-bold glow-text">
                {mintStartTimeLeft ? formatCountdown(mintStartTimeLeft) : "TBA"}
              </p>
            ) : (
              <p className="text-2xl font-bold glow-text">
                {phaseTimeLeft 
                  ? `${phaseTimeLeft.hours.toString().padStart(2, '0')}:${phaseTimeLeft.minutes.toString().padStart(2, '0')}:${phaseTimeLeft.seconds.toString().padStart(2, '0')}`
                  : nextPhaseToggleTimestamp ? "Phase Transition" : "Loading..."
                }
              </p>
            )}
            <motion.div
              className="cat-paw-indicator mt-2"
              initial={{ opacity: 0.7 }}
              animate={{ 
                opacity: [0.7, 1, 0.7]
              }}
              transition={{
                repeat: Infinity,
                duration: 2
              }}
            >
              <Cat className="h-6 w-6" />
            </motion.div>
          </motion.div>
        )}

        {/* Card 5: Reveal Countdown */}
        {revealTimestamp && (
          <motion.div 
            className="info-card relative min-w-[200px] h-40 p-5 rounded-lg border-0 flex flex-col justify-center items-center text-lg bg-gradient-to-br from-neon-yellow/20 to-neon-pink/20 overflow-hidden shadow-lg"
            initial="initial"
            animate="animate"
            whileHover="hover"
            whileTap="tap"
            variants={cardVariants}
          >
            {/* LED pulse border effect */}
            <div className="absolute inset-0 pointer-events-none">
              <motion.div 
                className="absolute inset-0 rounded-lg border-2 border-neon-yellow/70"
                animate={{
                  boxShadow: [
                    "0 0 2px var(--neon-yellow), 0 0 4px var(--neon-yellow)",
                    "0 0 6px var(--neon-yellow), 0 0 12px var(--neon-pink)",
                    "0 0 2px var(--neon-yellow), 0 0 4px var(--neon-yellow)"
                  ]
                }}
                transition={{ 
                  duration: 3, 
                  repeat: Infinity, 
                  ease: "easeInOut",
                  delay: 2
                }}
              />
            </div>
            
            <motion.div 
              className="cat-ears mb-2"
              variants={catEarVariants}
              initial="initial"
              animate="wiggle"
            >
              <Sparkles className="h-8 w-8 text-neon-yellow" />
            </motion.div>
            <h3 className="font-bold mb-2">Purrtardio Reveal</h3>
            {revealTimestamp.revealed ? (
              <p className="text-2xl font-bold text-success glow-text">Revealed!</p>
            ) : (
              <p className="text-2xl font-bold glow-text">
                {formatCountdown(revealTimeLeft)}
              </p>
            )}
            {!revealTimestamp.revealed && (
              <motion.div
                className="mystery-box mt-2"
                animate={{ 
                  opacity: [0.8, 1, 0.8]
                }}
                transition={{
                  repeat: Infinity,
                  duration: 2
                }}
              >
                <span className="text-2xl">🎁</span>
              </motion.div>
            )}
          </motion.div>
        )}
      </div>
      
      <div className="flex justify-center gap-4 flex-wrap">
        <motion.button
          className="retro-button bg-secondary"
          onClick={onConnect}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {isConnecting ? (
            <span className="flex items-center">
              <Sparkles className="animate-spin mr-2" />
              Connecting...
            </span>
          ) : isConnected ? (
            <span className="flex items-center">
              <Wallet className="mr-2" />
              {shortenAddress(address!)}
            </span>
          ) : (
            <span className="flex items-center">
              <Wallet className="mr-2" />
              Connect Wallet
            </span>
          )}
        </motion.button>
        
        {/* Character toggle button */}
        <motion.button
          className={`retro-button ${charactersEnabled ? 'bg-gradient-to-r from-neon-purple to-neon-pink' : 'bg-gray-700'}`}
          onClick={onToggleCharacters}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <span className="flex items-center">
            <MessageSquare className="mr-2" />
            {charactersEnabled ? 'GO SLEEP PURRTARDIOS' : 'WAKE UP PURRTARDIOS'}
          </span>
        </motion.button>
        
        {/* Special access button for PURRTARDIO holders */}
        {isConnected && hasNFTs && (
          <motion.button
            className="retro-button bg-primary text-white special-glow relative group"
            onClick={handleSpecialAccess}
            whileHover={{ 
              scale: 1.05, 
              boxShadow: "0 0 15px var(--neon-pink), 0 0 25px var(--neon-blue)" 
            }}
            whileTap={{ scale: 0.95 }}
            animate={{ 
              opacity: 1,
              y: 0,
              boxShadow: ["0 0 8px var(--neon-pink)", "0 0 20px var(--neon-pink)", "0 0 8px var(--neon-pink)"],
            }}
            initial={{ opacity: 0, y: 5 }}
            transition={{
              opacity: { duration: 0.4 },
              y: { duration: 0.4 },
              boxShadow: {
                repeat: Infinity,
                duration: 2
              }
            }}
          >
            <span className="flex items-center">
              <Star className="mr-2" />
              Special Access
            </span>
            <div className="absolute -top-16 left-1/2 transform -translate-x-1/2 w-48 p-2 bg-black/80 border border-primary rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10">
              Exclusive access for PURRTARDIO holders only! Click to enter the Purrdom area.
            </div>
          </motion.button>
        )}
      </div>
    </header>
  );
}